import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BudgetFormWithComments } from "../../components/BudgetForm";

import {
  CreditFormData,
  useCreditSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import {
  ClientBudgetProvider,
  CreditBudgetPayload,
} from "@/lib/contexts/ClientBudget/ClientBudgetProvider";
import { useClientBudget } from "@/lib/hooks/useClientBudget";
import { ClientType } from "@/types/client/ClientType";

interface CreditBudgetDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  client?: ClientType;
}

const CreditBudgetDialogContent = ({
  onOpenChange,
  onSuccess,
  client,
  clientId,
  open,
}: CreditBudgetDialogProps) => {
  const { t } = useTranslation();
  const { creditBudget } = useClientBudget();
  const { creditSchema } = useCreditSchema();
  const [canClose, setCanClose] = useState(true);
  const form = useForm<CreditFormData>({
    resolver: zodResolver(creditSchema),
    mode: "onChange",
  });

  const handleSubmit = (data: CreditFormData) => {
    const payload: CreditBudgetPayload = {
      clientId: clientId,
      payload: {
        amount: data?.budget,
        comments: data.comments,
      },
    };

    creditBudget.mutate(payload.payload, {
      onSuccess: () => {
        onSuccess();
        onOpenChange(false);
        form.reset();
        form.clearErrors();
      },
    });
  };

  useEffect(() => {
    if (creditBudget.isPending) {
      setCanClose(false);
    } else {
      setCanClose(true);
    }
  }, [creditBudget.isPending]);

  useEffect(() => {
    if (!open) {
      form.reset();
      form.clearErrors();
    }
  }, [open, form]);

  const errorCode = creditBudget.error?.response?.data?.[0]?.errorCode;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && canClose) {
      onOpenChange(false);
      form.reset();
      form.clearErrors();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={canClose}
        onInteractOutside={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{client?.name}</DialogTitle>
        </DialogHeader>
        <BudgetFormWithComments
          label={`${t("common.credit")} ${t("common.budget")}`}
          methods={form}
          onSubmit={handleSubmit}
          error={errorCode && t(`errorCodes.${errorCode}`)}
        />
        <DialogFooter className="">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={!canClose}
          >
            <X className="size-4" />
            {t("common.cancel")}
          </Button>
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            disabled={creditBudget.isPending || !canClose}
          >
            {creditBudget.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            {t("actions.save", { entity: t("common.credit") })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CreditBudgetDialogInner = (props: CreditBudgetDialogProps) => {
  return (
    <ClientBudgetProvider clientId={props.clientId}>
      <CreditBudgetDialogContent {...props} />
    </ClientBudgetProvider>
  );
};
